import { faArrowUpFromBracket, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Button, Flex, Icon, IconButton, Text } from '@scale/scaleui-radix';
import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';

const DEFAULT_MAX_FILE_SIZE_MB = 10;

const BYTES_IN_A_KILOBYTE = 10 ** 3;
const BYTES_IN_A_MEGABYTE = 10 ** 6;

interface FileUploadProps {
  files: File[];
  setFiles: (files: File[]) => void;
  maxNumFiles?: number;
  maxFileSizeMb?: number;
}

// TODO <cathy-scale>: add loading state
// TODO <cathy-scale>: maybe add cap on number of files
export default function FileUpload({
  files,
  setFiles,
  maxFileSizeMb = DEFAULT_MAX_FILE_SIZE_MB,
}: FileUploadProps) {
  const [error, setError] = useState<string | undefined>(undefined);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(undefined);
      if (rejectedFiles.length) {
        const rejectedFile = rejectedFiles[0];
        const acceptedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
        if (!acceptedTypes.includes(rejectedFile.file.type)) {
          setError('Please upload files in .pdf, .png, .jpg, or .jpeg format.');
        } else if (rejectedFile.file.size > maxFileSizeMb * 1024 * 1024) {
          setError(`File is too large. Please upload a file smaller than ${maxFileSizeMb}MB.`);
        } else {
          setError('Error uploading file. Please try again.');
        }
      }
      setFiles([...files, ...acceptedFiles]);
    },
    [files, maxFileSizeMb, setFiles],
  );

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxSize: maxFileSizeMb * 1024 * 1024,
  });

  return (
    <Flex direction="column" gap="2" className="scale-tailwind">
      <Button size="2" variant="outline" className="w-fit" {...getRootProps()} color="neutral">
        <input {...getInputProps()} />
        <Icon
          icon={faArrowUpFromBracket}
          size="2"
          label="arrow-up-from-bracket"
          className="text-neutral-9"
        />
        <Text className="text-neutral-12">Upload files</Text>
      </Button>
      <Text
        size="1"
        className="text-neutral-9"
      >{`Supported file types: .pdf, .png, .jpg, .jpeg (up to ${maxFileSizeMb}MB each)`}</Text>
      {files.map((file, index) => (
        <Flex key={index} gap="2" align="center">
          <Text size="2">{file.name}</Text>
          <Text size="2" className="text-neutral-9">
            {`(${getReadableFileSize(file)})`}
          </Text>
          <IconButton
            size="1"
            radius="full"
            variant="ghost"
            color="neutral"
            onClick={() => handleRemoveFile(index)}
          >
            <Icon icon={faTrash} size="1" label="trash" className="text-neutral-9" />
          </IconButton>
        </Flex>
      ))}
      {error && (
        <Text size="1" className="text-error-10">
          {error}
        </Text>
      )}
    </Flex>
  );
}

const getReadableFileSize = (file: File) => {
  if (file.size > BYTES_IN_A_MEGABYTE) {
    return `${(file.size / BYTES_IN_A_MEGABYTE).toFixed(2)} MB`;
  } else {
    return `${(file.size / BYTES_IN_A_KILOBYTE).toFixed(2)} KB`;
  }
};
